/**
 * Qui célèbre la fin d'une activité, et avec quels mots.
 *
 * `getChampion`, `getCelebrationMessage` et `getIsFastCombo` sont déplacés tels quels de
 * `student/activity/execute/ActivityFinalizedHelpers.js` (qui les réexporte pour l'ancien écran de
 * fin) : seuls des types sont ajoutés, et le tirage peut recevoir son hasard (un test passe le sien).
 */
import {
    abouCelebrations,
    samCelebrations,
    tinaCelebrations,
    zoeCelebrations,
    type CelebrationMessage,
} from "./celebrationBanks"

export type CelebrationChampion = "zoe" | "abou" | "sam" | "tina"

export const getChampion = (
    user: { id?: number | null } | null | undefined,
    today = new Date()
): CelebrationChampion => {
    const champions: CelebrationChampion[] = ["zoe", "abou", "sam", "tina"]

    const currentDay = today.getDate()

    const userIdNum = user?.id as number

    const championIndex = (userIdNum + currentDay) % champions.length

    return champions[championIndex] as CelebrationChampion
}

export const getCelebrationMessage = (
    champion: string,
    activityCount: number,
    isFirstActivity = false,
    isFastCombo = false,
    random: () => number = Math.random
): CelebrationMessage | null | undefined => {
    const celebrationBanks: Record<string, CelebrationMessage[]> = {
        sam: samCelebrations,
        tina: tinaCelebrations,
        zoe: zoeCelebrations,
        abou: abouCelebrations,
    }
    const bank = celebrationBanks[champion]

    if (!bank) {
        return null
    }

    if (isFirstActivity) {
        const rareFirst = bank.filter((msg) => msg.level === "rare" && msg.trigger === "first_activity")

        return rareFirst[Math.floor(random() * rareFirst.length)]
    }

    if (isFastCombo) {
        const rareCombo = bank.filter((msg) => msg.level === "rare" && msg.trigger === "fast_combo")
        return rareCombo[Math.floor(random() * rareCombo.length)]
    }

    let level = "intro"

    if (activityCount === 1) level = "intro"
    else if (activityCount <= 3) level = "boost"
    else if (activityCount <= 5) level = "momentum"
    else level = "performance"

    const eligible = bank.filter((msg) => msg.level === level)

    return eligible[Math.floor(random() * eligible.length)]
}

export const getIsFastCombo = (finishedActivities: { finished_at?: string | null }[]) => {
    if (finishedActivities.length < 2) {
        return false
    }

    const lastActivity = finishedActivities[0]
    const beforeLastActivity = finishedActivities[1]

    const end1 = new Date(beforeLastActivity?.finished_at as string).getTime()
    const end2 = new Date(lastActivity?.finished_at as string).getTime()

    const diffInSeconds = (end2 - end1) / 1000

    return diffInSeconds < 120
}
